import { useEffect, useRef, useState } from 'react'
import { Deck, MapView as DeckMapView } from '@deck.gl/core'
import { TileLayer } from '@deck.gl/geo-layers'
import { IconLayer, TextLayer, BitmapLayer } from '@deck.gl/layers'
import { X, AlertTriangle, Ship } from 'lucide-react'
import { Vessel, VesselDetails } from '../core/_models'
import { getVesselDetails } from '../core/_requests'
import { coordinateTextToDecimal, decimalToDmsString, validateCoordinateString } from '../../../shared/utils/coordinateParsing'
import VesselLandingLink from '../../../shared/components/VesselLandingLink'

interface MapViewProps {
  vesselMarkers: Vessel[]
  height?: string | number
  compact?: boolean
  vesselCount?: number
}

type PanelRisk = {
  label: string
  level: 'High' | 'Medium' | 'Low'
  colorClass: string
  iconClass: string
}

type PanelState = {
  vesselId?: number
  name: string
  imo: string
  mmsi: string
  status: string
  statusBg: string
  statusText: string
  statusBorder: string
  dotColor: string
  dest: string
  eta: string
  dist: string
  speed: string
  course: string
  fuel: string
  weather: string
  risks: PanelRisk[]
  lat: number
  lng: number
  // Raw backend coordinate strings + validation
  rawLat: string
  rawLng: string
  latWarning: string | null
  lngWarning: string | null
  // New fields from API
  lastPort: string
  atd: string
  latLongSource: string
  lastUpdated: string
  masterName: string
  chiefEngineerName: string
  cargoWeight: string
  agentName: string
  agentContact: string
  agentTelephone: string
  agentFax: string
  agentEmail: string
  draftFore: string
  draftMid: string
  draftAft: string
}

type MarkerPayload = {
  vessel: Vessel
  detail?: VesselDetails
  lat: number
  lng: number
  course: number
  statusColorHex: string
  panel: PanelState
}

const CARTO_API_KEY = process.env.REACT_APP_CARTO_API_KEY || 'cb1_2a53_1_ef9a2298faa15ac3bde3fdb3'
const CARTO_TILE_URL = `https://c.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png?key=${CARTO_API_KEY}`

const getHash = (value: string): number => {
  let hash = 0
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(i)
    hash |= 0
  }
  return Math.abs(hash)
}

const formatDateSafe = (raw?: string | null, includeUtc = false): string => {
  if (!raw || raw === '—' || raw === '--:--' || raw === 'Unknown' || raw === 'null' || raw === 'Invalid Date') return '—'
  const d = new Date(raw)
  if (isNaN(d.getTime())) return raw !== 'Invalid Date' ? raw : '—'
  const formatted = d.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  })
  return includeUtc ? `${formatted} UTC` : formatted
}

const titleCaseStatus = (value?: string): string => {
  const text = String(value || 'Unknown').trim().toLowerCase()
  if (!text) return 'Unknown'
  if (text.includes('anchor')) return 'Anchor'
  if (text.includes('port') || text.includes('moor') || text.includes('berth')) return 'In Port'
  if (text.includes('underway') || text.includes('at sea') || text.includes('sea') || text.includes('sailing')) return 'At Sea'
  if (text.includes('maint')) return 'Maintenance'
  if (text.includes('idle')) return 'Idle'
  return text.replace(/\b\w/g, (c) => c.toUpperCase())
}

const panelColors = (status: string) => {
  const normalized = status.toLowerCase()
  if (normalized.includes('maint')) {
    return {
      statusBg: 'bg-red-50',
      statusText: 'text-red-700',
      statusBorder: 'border-red-100',
      dotColor: 'bg-red-500',
      hex: '#ef4444',
    }
  }
  if (normalized.includes('anchor')) {
    return {
      statusBg: 'bg-amber-50',
      statusText: 'text-amber-700',
      statusBorder: 'border-amber-100',
      dotColor: 'bg-amber-500',
      hex: '#f59e0b',
    }
  }
  if (normalized.includes('port') || normalized.includes('moor') || normalized.includes('berth')) {
    return {
      statusBg: 'bg-sky-50',
      statusText: 'text-sky-700',
      statusBorder: 'border-sky-100',
      dotColor: 'bg-sky-500',
      hex: '#0ea5e9',
    }
  }
  if (normalized.includes('idle')) {
    return {
      statusBg: 'bg-slate-50',
      statusText: 'text-slate-700',
      statusBorder: 'border-slate-200',
      dotColor: 'bg-slate-500',
      hex: '#64748b',
    }
  }
  return {
    statusBg: 'bg-emerald-50',
    statusText: 'text-emerald-700',
    statusBorder: 'border-emerald-100',
    dotColor: 'bg-emerald-500',
    hex: '#10b981',
  }
}

/**
 * Resolves real coordinate reports from backend API response.
 * Strictly returns null if no valid latitude/longitude report exists (NO dummy/hash positions).
 */
const resolveCoords = (vessel: Vessel, detail?: VesselDetails): [number, number] | null => {
  // 1. Try detail lat/long from landing API
  if (detail) {
    const dLat = coordinateTextToDecimal(detail.latitude, 'lat')
    const dLng = coordinateTextToDecimal(detail.longitude, 'lon')
    if (dLat != null && dLng != null) {
      return [dLat, dLng]
    }
  }

  // 2. Try vessel-level lat/long properties from summary & report DTOs
  const vAny = vessel as any
  const vLat = coordinateTextToDecimal(
    vessel.latitude ?? vAny.lat ?? vAny.positionLat ?? vAny.position_lat ?? vAny.reportLatitude,
    'lat'
  )
  const vLng = coordinateTextToDecimal(
    vessel.longitude ?? vAny.lng ?? vAny.positionLon ?? vAny.position_lon ?? vAny.reportLongitude,
    'lon'
  )

  if (vLat != null && vLng != null) {
    return [vLat, vLng]
  }

  // 3. Try vessel.coords tuple [lng, lat]
  if (Array.isArray(vessel.coords) && vessel.coords.length >= 2) {
    const [cLng, cLat] = vessel.coords
    const parsedLat = coordinateTextToDecimal(cLat, 'lat')
    const parsedLng = coordinateTextToDecimal(cLng, 'lon')
    if (parsedLat != null && parsedLng != null) {
      return [parsedLat, parsedLng]
    }
  }

  // Strictly return null if no real lat/long report is present
  return null
}

const createPanelState = (vessel: Vessel, detail: VesselDetails | undefined, lat: number, lng: number): PanelState => {
  const status = titleCaseStatus(detail?.status || vessel.status)
  const colors = panelColors(status)
  const hash = getHash(vessel.fleet_name)

  const imo = detail?.imoNumber || vessel.imoNumber || '—'
  const mmsi = detail?.mmsi || vessel.mmsi || '—'
  const dest = detail?.nextPort || vessel.port || 'Unknown'
  const eta = detail?.eta || vessel.eta || 'Unknown'
  const dist = '—'
  const speed = detail?.speed || '—'
  const course = detail?.course || '—'
  const fuel = `${55 + (hash % 35)}%`
  const weather = `${14 + (hash % 18)}°C`

  const lastPort = detail?.lastPort || '—'
  const atd = detail?.atd || '—'
  const latLongSource = detail?.latLongSource || '—'
  const lastUpdated = detail?.lastUpdated || '—'
  const masterName = detail?.masterName || '—'
  const chiefEngineerName = detail?.chiefEngineerName || '—'
  const cargoWeight = detail?.cargoWeight || '—'
  const agentName = detail?.agentName || '—'
  const agentContact = detail?.agentContactNumber || '—'
  const agentTelephone = detail?.agentTelephoneNumber || '—'
  const agentFax = detail?.agentFaxAddress || '—'
  const agentEmail = detail?.agentEmail || '—'
  const draftFore = detail?.draftFore || '—'
  const draftMid = detail?.draftMid || '—'
  const draftAft = detail?.draftAft || '—'

  const rawLat = (detail?.latitude ?? '').trim()
  const rawLng = (detail?.longitude ?? '').trim()
  const latWarning = rawLat && rawLat !== '—' ? validateCoordinateString(rawLat, 'lat').message ?? null : null
  const lngWarning = rawLng && rawLng !== '—' ? validateCoordinateString(rawLng, 'lon').message ?? null : null

  const risks: PanelRisk[] = []
  const isActive = !status.toLowerCase().includes('maintenance')
  if (!isActive || status.toLowerCase().includes('maintenance')) {
    risks.push(
      { label: 'Main engine inspection overdue', level: 'High', colorClass: 'text-red-600', iconClass: 'text-red-500' },
      { label: 'Port audit in 2 days', level: 'Medium', colorClass: 'text-orange-600', iconClass: 'text-orange-500' },
    )
  } else if (hash % 3 === 0) {
    risks.push(
      { label: 'Fuel pump spare delayed', level: 'High', colorClass: 'text-red-600', iconClass: 'text-red-500' },
      { label: 'Crew certificate expiring', level: 'Medium', colorClass: 'text-orange-600', iconClass: 'text-orange-500' },
    )
  } else if (hash % 2 === 0) {
    risks.push(
      { label: 'Port inspection scheduled soon', level: 'Medium', colorClass: 'text-orange-600', iconClass: 'text-orange-500' },
    )
  }

  return {
    vesselId: vessel.id,
    name: vessel.fleet_name,
    imo,
    mmsi,
    status,
    statusBg: colors.statusBg,
    statusText: colors.statusText,
    statusBorder: colors.statusBorder,
    dotColor: colors.dotColor,
    dest,
    eta,
    dist,
    speed,
    course,
    fuel,
    weather,
    risks,
    lat,
    lng,
    rawLat,
    rawLng,
    latWarning,
    lngWarning,
    lastPort,
    atd,
    latLongSource,
    lastUpdated,
    masterName,
    chiefEngineerName,
    cargoWeight,
    agentName,
    agentContact,
    agentTelephone,
    agentFax,
    agentEmail,
    draftFore,
    draftMid,
    draftAft,
  }
}

const createSvgIconDataUrl = (colorHex: string) => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="${colorHex}"><path d="M4.037 4.688a.495.495 0 0 1 .651-.651l16 6.5a.5.5 0 0 1-.063.947l-6.124 1.58a2 2 0 0 0-1.438 1.435l-1.579 6.126a.5.5 0 0 1-.947.063z"/></svg>`
  return 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg)
}

export default function MapView({ vesselMarkers, height = '100%', compact = false, vesselCount }: MapViewProps) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const deckRef = useRef<Deck<any> | null>(null)
  const selectedPanelRef = useRef<string | null>(null)
  const [selectedPanel, setSelectedPanel] = useState<PanelState | null>(null)
  const [panelVisible, setPanelVisible] = useState(false)
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailsMap, setDetailsMap] = useState<Record<number, VesselDetails>>({})
  const [webglUnavailable, setWebglUnavailable] = useState(false)

  useEffect(() => {
    selectedPanelRef.current = selectedPanel?.name ?? null
  }, [selectedPanel])

  // Automatically fetch landing details for vessels that do not have coordinates directly on the summary object
  useEffect(() => {
    let active = true
    const unmappedVessels = vesselMarkers.filter((vessel) => {
      const existingDetail = detailsMap[vessel.id]
      const coords = resolveCoords(vessel, existingDetail)
      return !coords
    })

    if (!unmappedVessels.length) return

    Promise.all(
      unmappedVessels.map(async (vessel) => {
        try {
          const detail = await getVesselDetails(vessel.id)
          return { id: vessel.id, detail }
        } catch {
          return null
        }
      })
    ).then((results) => {
      if (!active) return
      const updates: Record<number, VesselDetails> = {}
      results.forEach((res) => {
        if (res?.detail) {
          updates[res.id] = res.detail
        }
      })
      if (Object.keys(updates).length > 0) {
        setDetailsMap((prev) => ({ ...prev, ...updates }))
      }
    })

    return () => {
      active = false
    }
  }, [vesselMarkers])

  // Initialize DeckGL instance
  useEffect(() => {
    if (!containerRef.current || deckRef.current) return

    const tileLayer = new TileLayer({
      id: 'carto-basemap-tiles',
      data: CARTO_TILE_URL,
      minZoom: 0,
      maxZoom: 19,
      tileSize: 256,
      repeatLongitude: true,
      renderSubLayers: (props: any) => {
        const { boundingBox } = props.tile
        return new BitmapLayer(props, {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore – BitmapLayer data must be null in image mode; typings incorrectly infer 'never'
          data: null,
          image: props.data,
          bounds: [boundingBox[0][0], boundingBox[0][1], boundingBox[1][0], boundingBox[1][1]],
        })
      },
    })

    let deck: Deck<any>
    try {
      deck = new Deck({
        parent: containerRef.current,
        views: new DeckMapView({ id: 'map', repeat: true }),
        initialViewState: {
          longitude: 20,
          latitude: 20,
          zoom: 2,
          pitch: 0,
          bearing: 0,
        },
        controller: true,
        layers: [tileLayer],
        onError: (error) => {
          console.error('Fleet map WebGL initialization failed:', error)
          setWebglUnavailable(true)
        },
      })
    } catch (error) {
      console.error('Fleet map WebGL initialization failed:', error)
      setWebglUnavailable(true)
      return
    }

    deckRef.current = deck

    return () => {
      deck.finalize()
      deckRef.current = null
    }
  }, [])

  // Update vessel layers whenever vesselMarkers or detailsMap changes
  useEffect(() => {
    const deck = deckRef.current
    if (!deck) return

    // Include ALL vessels (At Sea, In Port, Anchor, Maintenance, Idle) with valid coordinate reports
    const built: MarkerPayload[] = vesselMarkers
      .map((vessel) => {
        const detail = detailsMap[vessel.id]
        const coords = resolveCoords(vessel, detail)
        if (!coords) return null
        const [lat, lng] = coords
        const panel = createPanelState(vessel, detail, lat, lng)
        const colors = panelColors(panel.status)

        const rawCourse = panel.course ?? '0'
        const course = parseFloat(String(rawCourse).replace(/[°\s]/g, '')) || 0

        return {
          vessel,
          detail,
          lat,
          lng,
          course,
          statusColorHex: colors.hex,
          panel,
        } as MarkerPayload
      })
      .filter(Boolean) as MarkerPayload[]

    if (!built.length) {
      setSelectedPanel(null)
      return
    }

    const handleVesselClick = (info: any) => {
      if (info && info.object) {
        const item = info.object as MarkerPayload
        setPanelVisible(true)
        setSelectedPanel(item.panel)
        setDetailLoading(true)
        getVesselDetails(item.vessel.id)
          .then((detail) => {
            if (detail) {
              const coords = resolveCoords(item.vessel, detail)
              const [lat, lng] = coords ?? [item.lat, item.lng]
              setSelectedPanel(createPanelState(item.vessel, detail, lat, lng))
            }
            setDetailLoading(false)
          })
          .catch(() => setDetailLoading(false))
      }
    }

    const tileLayer = new TileLayer({
      id: 'carto-basemap-tiles',
      data: CARTO_TILE_URL,
      minZoom: 0,
      maxZoom: 19,
      tileSize: 256,
      repeatLongitude: true,
      renderSubLayers: (props: any) => {
        const { boundingBox } = props.tile
        return new BitmapLayer(props, {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore – BitmapLayer data must be null in image mode; typings incorrectly infer 'never'
          data: null,
          image: props.data,
          bounds: [boundingBox[0][0], boundingBox[0][1], boundingBox[1][0], boundingBox[1][1]],
        })
      },
    })

    const vesselIconLayer = new IconLayer({
      id: 'vessel-icon-layer',
      data: built,
      pickable: true,
      wrapLongitude: true,
      onClick: handleVesselClick,
      getPosition: (d: MarkerPayload) => [d.lng, d.lat],
      getIcon: (d: MarkerPayload) => ({
        url: createSvgIconDataUrl(d.statusColorHex),
        width: 32,
        height: 32,
        anchorX: 16,
        anchorY: 16,
      }),
      getSize: () => 28,
      getAngle: (d: MarkerPayload) => -d.course,
    })

    const vesselLabelLayer = new TextLayer({
      id: 'vessel-label-layer',
      data: built,
      pickable: true,
      wrapLongitude: true,
      onClick: handleVesselClick,
      getPosition: (d: MarkerPayload) => [d.lng, d.lat],
      getText: (d: MarkerPayload) => `${d.vessel.fleet_name}\n${d.panel.status && d.panel.status !== 'Unknown' ? d.panel.status : ''}`,
      getSize: 11,
      getColor: [15, 23, 42, 255],
      getBackgroundColor: [255, 255, 255, 220],
      backgroundPadding: [4, 2],
      characterSet: 'auto',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontWeight: 'bold',
      getPixelOffset: [0, 22],
    })

    deck.setProps({
      layers: [tileLayer, vesselIconLayer, vesselLabelLayer],
    })

    if (selectedPanelRef.current && built.some((item) => item.panel.name === selectedPanelRef.current)) {
      const existing = built.find((item) => item.panel.name === selectedPanelRef.current)
      if (existing) setSelectedPanel(existing.panel)
    }
  }, [vesselMarkers, detailsMap])

  const panel = selectedPanel
    ? selectedPanel
    : {
      name: '',
      imo: '',
      mmsi: '',
      status: 'Loading',
      statusBg: 'bg-emerald-50',
      statusText: 'text-emerald-700',
      statusBorder: 'border-emerald-100',
      dotColor: 'bg-emerald-500',
      dest: '',
      eta: '',
      dist: '348 NM',
      speed: '12.4',
      course: '182°',
      fuel: '72%',
      weather: '18°C',
      risks: [
        { label: 'Main engine inspection overdue', level: 'High', colorClass: 'text-red-600', iconClass: 'text-red-500' },
        { label: 'Port audit in 2 days', level: 'Medium', colorClass: 'text-orange-600', iconClass: 'text-orange-500' },
      ],
      lat: 20,
      lng: 72,
      rawLat: '',
      rawLng: '',
      latWarning: null,
      lngWarning: null,
      lastPort: '—',
      atd: '—',
      latLongSource: '—',
      lastUpdated: '—',
      masterName: '—',
      chiefEngineerName: '—',
      cargoWeight: '—',
      agentName: '—',
      agentContact: '—',
      agentTelephone: '—',
      agentFax: '—',
      agentEmail: '—',
      draftFore: '—',
      draftMid: '—',
      draftAft: '—',
    }

  return (
    <div className={`relative h-full w-full overflow-hidden bg-[#dfeef2] ${compact ? 'rounded-2xl' : ''}`}>
      <div
        id="fleet-map"
        ref={containerRef}
        className={`absolute inset-0 overflow-hidden ${compact ? 'rounded-2xl' : ''}`}
        style={{ height }}
      />

      {webglUnavailable && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-[#dfeef2] p-6 text-center">
          <div className="max-w-sm rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <AlertTriangle className="mx-auto mb-3 h-8 w-8 text-amber-500" />
            <div className="font-semibold text-slate-800">Map unavailable</div>
            <div className="mt-1 text-sm text-slate-500">
              WebGL2 is unavailable or disabled in this browser or environment.
            </div>
          </div>
        </div>
      )}

      {panelVisible && (
        <div className="absolute top-4 right-4 z-[500] w-[340px] max-w-[calc(100%-2rem)] max-h-[calc(100%-2rem)] overflow-y-auto rounded-[22px] bg-white shadow-[0_16px_50px_rgba(15,23,42,0.12)] border border-slate-100 p-4">
          {detailLoading ? (
            <div className="space-y-3 p-1 animate-pulse">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0">
                    <Ship className="w-6 h-6 text-slate-300" />
                  </div>
                  <div className="space-y-1.5">
                    <div className="h-4 bg-slate-200 rounded w-36" />
                    <div className="h-3 bg-slate-100 rounded w-20" />
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setPanelVisible(false)}
                  className="w-8 h-8 rounded-full hover:bg-slate-50 flex items-center justify-center text-slate-400 hover:text-slate-600 transition-colors"
                  aria-label="Close panel"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* IMO & Status */}
              <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
                <div className="h-[52px] bg-slate-50 rounded-xl" />
                <div className="h-[52px] bg-slate-50 rounded-xl" />
              </div>

              {/* Last Port / Next Port / ATD / ETA */}
              <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                <div className="h-[52px] bg-slate-50 rounded-xl" />
                <div className="h-[52px] bg-slate-50 rounded-xl" />
                <div className="h-[52px] bg-slate-50 rounded-xl" />
                <div className="h-[52px] bg-slate-50 rounded-xl" />
              </div>

              {/* Ship Latitude and Longitude */}
              <div className="mt-3 h-16 bg-slate-50 rounded-xl" />

              {/* Data Source & Last Updated */}
              <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                <div className="h-[52px] bg-slate-50 rounded-xl" />
                <div className="h-[52px] bg-slate-50 rounded-xl" />
              </div>

              {/* Master & CE Name */}
              <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                <div className="h-[52px] bg-slate-50 rounded-xl" />
                <div className="h-[52px] bg-slate-50 rounded-xl" />
              </div>

              {/* Agent Information */}
              <div className="mt-3">
                <div className="h-3 bg-slate-200 rounded w-28 mb-2" />
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="h-[52px] bg-slate-50 rounded-xl" />
                  <div className="h-[52px] bg-slate-50 rounded-xl" />
                  <div className="h-[52px] bg-slate-50 rounded-xl" />
                  <div className="h-[52px] bg-slate-50 rounded-xl" />
                  <div className="h-[52px] bg-slate-50 rounded-xl col-span-2" />
                </div>
              </div>

              <div className="text-center py-2 text-xs text-slate-400 font-medium animate-pulse">
                Loading full vessel details…
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  <div className="w-12 h-12 rounded-xl overflow-hidden bg-slate-100 flex items-center justify-center flex-shrink-0">
                    <Ship className="w-6 h-6 text-slate-400" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-lg font-semibold text-slate-900 leading-tight truncate">
                      {panel.vesselId ? (
                        <VesselLandingLink
                          vesselId={panel.vesselId}
                          label={panel.name}
                          className="text-slate-900 hover:text-blue-600 underline font-semibold"
                          title="View Full Vessel Details"
                        />
                      ) : (
                        panel.name
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <span id="panel-status" className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-semibold ${panel.statusBg} ${panel.statusText} ${panel.statusBorder}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${panel.dotColor}`} />
                        {panel.status}
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setPanelVisible(false)}
                  className="w-8 h-8 rounded-full hover:bg-slate-50 flex items-center justify-center text-slate-400 hover:text-slate-600 transition-colors"
                  aria-label="Close panel"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Vessel IMO & MMSI */}
              <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
                <div className="rounded-xl bg-slate-50 p-2.5">
                  <div className="text-slate-400">Vessel IMO</div>
                  <div className="mt-1 text-sm font-semibold text-slate-900">{panel.imo}</div>
                </div>
                <div className="rounded-xl bg-slate-50 p-2.5">
                  <div className="text-slate-400">Vessel Status</div>
                  <div className="mt-1 text-sm font-semibold text-slate-900">{panel.status}</div>
                </div>
              </div>

              {/* Last Port / Next Port / ATD / ETA */}
              <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                <div className="rounded-xl bg-slate-50 p-2.5">
                  <div className="text-slate-400">Last Port</div>
                  <div className="mt-1 text-sm font-semibold text-slate-900">{panel.lastPort}</div>
                </div>
                <div className="rounded-xl bg-slate-50 p-2.5">
                  <div className="text-slate-400">Next Port</div>
                  <div className="mt-1 text-sm font-semibold text-slate-900">{panel.dest}</div>
                </div>
                <div className="rounded-xl bg-slate-50 p-2.5">
                  <div className="text-slate-400">ATD</div>
                  <div className="mt-1 text-sm font-semibold text-slate-900">{formatDateSafe(panel.atd)}</div>
                </div>
                <div className="rounded-xl bg-slate-50 p-2.5">
                  <div className="text-slate-400">ETA</div>
                  <div className="mt-1 text-sm font-semibold text-slate-900">{formatDateSafe(panel.eta)}</div>
                </div>
              </div>

              {/* Ship Latitude and Longitude */}
              <div className="mt-3 rounded-xl bg-slate-50 p-2.5 text-xs">
                <div className="text-slate-400">Ship Latitude and Longitude</div>
                <div className="mt-1 text-sm font-semibold text-slate-900">
                  {panel.rawLat && panel.rawLng && panel.rawLat !== '—' && panel.rawLng !== '—'
                    ? `${panel.rawLat}, ${panel.rawLng}`
                    : `${decimalToDmsString(panel.lat, 'lat')}, ${decimalToDmsString(panel.lng, 'lon')}`}
                </div>
                {(panel.latWarning || panel.lngWarning) && (
                  <div className="mt-2 flex items-start gap-1.5 rounded-lg border border-red-100 bg-red-50 px-2 py-1.5 text-[11px] text-red-700">
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-red-500" />
                    <div className="min-w-0">
                      <div className="font-semibold">Invalid coordinates</div>
                      {panel.latWarning && <div className="font-normal">{panel.latWarning}</div>}
                      {panel.lngWarning && <div className="font-normal">{panel.lngWarning}</div>}
                    </div>
                  </div>
                )}
              </div>

              {/* Data Source & Last Updated */}
              <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                <div className="rounded-xl bg-slate-50 p-2.5">
                  <div className="text-slate-400">Data Source</div>
                  <div className="mt-1 text-sm font-semibold text-slate-900">{panel.latLongSource}</div>
                </div>
                <div className="rounded-xl bg-slate-50 p-2.5">
                  <div className="text-slate-400">Last Updated</div>
                  <div className="mt-1 text-sm font-semibold text-slate-900">{formatDateSafe(panel.lastUpdated, true)}</div>
                </div>
              </div>

              {/* Master & Chief Engineer */}
              <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                <div className="rounded-xl bg-slate-50 p-2.5">
                  <div className="text-slate-400">Master Name</div>
                  <div className="mt-1 text-sm font-semibold text-slate-900">{panel.masterName}</div>
                </div>
                <div className="rounded-xl bg-slate-50 p-2.5">
                  <div className="text-slate-400">CE Name</div>
                  <div className="mt-1 text-sm font-semibold text-slate-900">{panel.chiefEngineerName}</div>
                </div>
              </div>

              {/* Agent Info */}
              <div className="mt-3">
                <div className="text-[11px] font-semibold text-slate-700 mb-2">Agent Information</div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="rounded-xl bg-slate-50 p-2.5">
                    <div className="text-slate-400">Agent Name</div>
                    <div className="mt-1 text-sm font-semibold text-slate-900">{panel.agentName}</div>
                  </div>
                  <div className="rounded-xl bg-slate-50 p-2.5">
                    <div className="text-slate-400">Agent Contact</div>
                    <div className="mt-1 text-sm font-semibold text-slate-900">{panel.agentContact}</div>
                  </div>
                  <div className="rounded-xl bg-slate-50 p-2.5">
                    <div className="text-slate-400">Agent Telephone</div>
                    <div className="mt-1 text-sm font-semibold text-slate-900">{panel.agentTelephone}</div>
                  </div>
                  <div className="rounded-xl bg-slate-50 p-2.5">
                    <div className="text-slate-400">Agent Fax</div>
                    <div className="mt-1 text-sm font-semibold text-slate-900">{panel.agentFax}</div>
                  </div>
                  <div className="rounded-xl bg-slate-50 p-2.5 col-span-2">
                    <div className="text-slate-400">Agent Email</div>
                    <div className="mt-1 text-sm font-semibold text-slate-900">{panel.agentEmail}</div>
                  </div>
                </div>
              </div>

              {panel.vesselId && (
                <div className="mt-3 pt-2 border-t border-slate-100 flex justify-end">
                  <VesselLandingLink
                    vesselId={panel.vesselId}
                    label="View Full Vessel Details →"
                    className="text-xs font-semibold text-blue-600 hover:text-blue-800 hover:underline"
                  />
                </div>
              )}
            </>
          )}
        </div>
      )}

      <div className="absolute bottom-4 left-4 z-[50] flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white/95 px-3 py-2 shadow-sm backdrop-blur-sm">
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-emerald-500" />
          <span className="text-[11px] font-medium text-slate-600">At Sea</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-sky-500" />
          <span className="text-[11px] font-medium text-slate-600">In Port</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-amber-500" />
          <span className="text-[11px] font-medium text-slate-600">Anchor</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-red-500" />
          <span className="text-[11px] font-medium text-slate-600">Maintenance</span>
        </div>
        <button type="button" className="text-[11px] font-semibold text-blue-600 hover:underline">
          Fleet Vessels ({vesselCount ?? vesselMarkers.length})
        </button>
      </div>
    </div>
  )
}
